import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
  WritableSignal,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SplitAreaComponent, SplitComponent } from 'angular-split';
import {
  Observable,
  Subscription,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  of,
  startWith,
  switchMap,
  take
} from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';
import { showGlobalDataOnly } from '@gfe/core/store/plan/plan.actions';
import {
  AvailableFilterField,
  FilterConfiguration
} from '@gfe/shared/components/filter-query-builder/filter.models';

import {
  holdsHoles,
  templateParameters,
  withoutTemplateHoles
} from '@core/filters/path-filter/templates';
import { FilterField } from '@core/filters/path-filter/vocabulary/vocabulary';
import { FilterExpr, fieldPath } from '@core/filters/path-filter/wire';
import { AssistantTableQuery } from '@core/models/assistant/assistant.model';
import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import { DataspaceState } from '@core/models/dataspace/dataspace-state.model';
import {
  EntityCfg,
  EntityInterface,
  FilterableColCfg,
  makeAvailableFilterFields,
  makeFilterCfgMap,
  makeTranslatedFilterCfgMap
} from '@core/models/entities/entity-visualization-interface';
import {
  ROUTE_LOCK_OPTIONS,
  RouteLockCfg,
  RouteValidationState,
  isRouteValidated,
  routeValidationBlocker
} from '@core/models/entities/implementations/user-route-v1';
import { EntityMetadata } from '@core/models/entity/entity-metadata.model';
import {
  CreateOrUpdateGlobalEntityRequest,
  EntitySelectorRequestModel,
  PlanFilterOperator
} from '@core/models/entity/entity-selector-request.model';
import { PagedList } from '@core/models/paged-list';
import { CustomTransformerResponse } from '@core/models/transformers.model';
import { AssistantTableStateService } from '@core/services/assistant/assistant-table-state.service';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { CsvTransformerService } from '@core/services/csv-transformer/csv-transformer.service';
import { PagedEntityService } from '@core/services/entities/paged-entity.service';
import { EntityHydrationService } from '@core/services/entity-hydration/entity-hydration.service';
import { EntityLabelsTranslationService } from '@core/services/entity-labels-translation/entity-labels-translation.service';
import {
  EntityListFilterContext,
  EntityListFilterHandoffService
} from '@core/services/entity-list-filter-handoff/entity-list-filter-handoff.service';
import {
  EntityModificationService,
  MAX_BULK_PATCH_ITEMS
} from '@core/services/entity-modification/entity-modification.service';
import { ICONS } from '@core/services/icons/icons.service';
import { PlanAwareNavigationService } from '@core/services/navigation/plan-aware-navigation.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { ParamsService } from '@core/services/params/params.service';
import {
  ReferenceNamesService,
  ReferenceResolutionContext
} from '@core/services/reference-names/reference-names.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import { SplitColumnsService } from '@core/services/split-columns/split-columns.service';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';
import { UserModelsRegistryService } from '@core/services/user-models-registry/user-models-registry.service';
import {
  selectLabels,
  selectTags
} from '@core/store/admin/tags-labels/tags-labels.selectors';
import { selectIsPermissionEnabled } from '@core/store/auth/auth.selectors';
import { selectIsPopupOpen } from '@core/store/data-visualization-popup/data-visualization-popup.selectors';
import {
  entityModified,
  setVisualizedEntities,
  toggleCalendarVisualization,
  toggleListVisualization,
  toggleMapVisualization,
  toggleTimelineVisualization
} from '@core/store/data-visualization/data-visualization.actions';
import {
  selectAvailableEntities,
  selectAvailableTransformers,
  selectDataspaceState
} from '@core/store/dataspace/dataspace.selectors';
import {
  selectHiddenRouteUids,
  selectIsPlanInViewHeld,
  selectOpenedRouteUids,
  selectPendingRouteUids,
  selectPlanEditSession,
  selectPlanWatchForView
} from '@core/store/plan-edit/plan-edit-session.selectors';
import { PlanEditActions, PlanWatchActions } from '@core/store/plan-edit/plan-edit.actions';
import { liveFlavours } from '@core/store/plan-edit/plan-edit.state';
import { overlayEntityMap } from '@core/store/plan-edit/plan-watch-overlay';
import { selectPlan } from '@core/store/plan/plan.selectors';
import {
  FilterGrammar,
  VisualizationPreferences,
  resolveVisualizationPreferences
} from '@core/store/user-preferences/user-preferences.model';
import {
  selectTables,
  selectUserPreferencesLoaded,
  selectVisualizationPreferencesMap
} from '@core/store/user-preferences/user-preferences.selectors';
import { AutoUnsubscribe } from '@core/utils/auto-unsubscribe';
import { convertKeysToSnakeCase } from '@core/utils/case-conversions';
import { extractDataspaceIdFromUrl } from '@core/utils/dataspace-url.utils';
import { isPlanEditBlocked } from '@core/utils/plan-edit.utils';
import { isNil, sameJson } from '@core/utils/shared-utils';

import { DataVisuComponent } from '@shared/components/data-visu/data-visu.component';
import { FilterQueryBuilderComponent } from '@shared/components/filter-query-builder/filter-query-builder.component';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { PathFilterComposerComponent } from '@shared/components/path-filter/composer/path-filter-composer.component';
import { PathFilterPanelComponent } from '@shared/components/path-filter/panel/path-filter-panel.component';
import {
  PATH_FILTER_I18N,
  associationLabel,
  sortableLabel
} from '@shared/components/path-filter/path-filter-labels';
import {
  ComposerMode,
  NewParameterRequest,
  ParameterOption
} from '@shared/components/path-filter/path-filter.models';
import { PathFilterPillsComponent } from '@shared/components/path-filter/pills/path-filter-pills.component';
import { PathFilterSearchBarComponent } from '@shared/components/path-filter/search-bar/path-filter-search-bar.component';
import {
  SortOption,
  columnSortOptions,
  pathOrderBy,
  sortOptions,
  toOrderBy
} from '@shared/components/path-filter/sort/sort-options';
import { SortTabComponent } from '@shared/components/path-filter/sort/sort-tab/sort-tab.component';
import { ReferencePopupOutletComponent } from '@shared/components/reference-popup-outlet/reference-popup-outlet.component';
import { SearchResult } from '@shared/components/search-query-builder/search-query-builder.component';
import { SearchResultBuilder } from '@shared/components/search-query-builder/search-result-builder';
import { ReferenceNameLookup } from '@shared/components/table/table-content/table-content.component';
import { TableComponent } from '@shared/components/table/table.component';
import {
  RowEvent,
  ShowDetails,
  SortState,
  TableAction,
  TableColumn
} from '@shared/components/table/table.model';
import { ToolboxComponent } from '@shared/components/toolbox/toolbox.component';
import {
  UploadClicked,
  UploadZoneComponent
} from '@shared/components/upload-zone/upload-zone.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';
import { ReturnFocusDirective } from '@shared/directives/return-focus.directive';
import { AvailableLanguages } from '@shared/types/translation.type';
import {
  addOrReplaceConditionInConfig,
  createFilterCondition,
  findFilterField,
  removeConditionFromConfig
} from '@shared/utils/filter-condition.utils';
import {
  RestoredSort,
  SortableFieldRef,
  buildFilterPresetPayload
} from '@shared/utils/filter-preset.utils';
import {
  IncludeInFilterStatus,
  includeInFilterStatus,
  includeUidInFilter
} from '@shared/utils/include-in-filter.utils';
import { Pagination } from '@shared/utils/pagination.utils';
import { convertFilterConfigurationToBackEndFormat } from '@shared/utils/search-filter.utils';

import { EntityTableService } from '@features/components/entities/entity-table.service';
import { FilterPresetsComponent } from '@features/components/entities/filter-presets/filter-presets.component';
import {
  AppliedFilterPreset,
  FilterPresetsStore
} from '@features/components/entities/filter-presets/filter-presets.store';
import { FilterTemplateAuthorComponent } from '@features/components/entities/filter-presets/filter-template-author/filter-template-author.component';
import {
  qualifiersForSortState,
  sortStateFor,
  withHeaderSort
} from '@features/components/entities/path-filter-bar/column-sort';
import { PathFilterMoveButtonsComponent } from '@features/components/entities/path-filter-bar/move-buttons/path-filter-move-buttons.component';
import {
  PathFetch,
  PathFilterBarState
} from '@features/components/entities/path-filter-bar/path-filter-bar.state';
import { PlanEditBannerComponent } from '@features/components/plan/edit/plan-edit-banner/plan-edit-banner.component';
import { PlanEditBarComponent } from '@features/components/plan/edit/plan-edit-bar/plan-edit-bar.component';
import { openPlanEdit } from '@features/components/plan/edit/plan-edit-toolbox-item';

import {
  DetailGroup,
  activateDetail,
  activeDetailOf,
  closeDetail,
  closeGroup,
  findGroupIndex,
  moveDetail,
  openDetail,
  replaceEntity
} from '../../entity-details/entity-detail-groups';
import { EntityDetailTabsComponent } from '../../entity-details/entity-detail-tabs/entity-detail-tabs.component';
import {
  EntityDetailInput,
  EntityDetailsComponent
} from '../../entity-details/entity-details.component';
import {
  EntitySummary,
  PASSIVE_SUMMARY_MAX_ROWS,
  SELECT_ALL_SUMMARY_MAX_ROWS,
  SummaryStatus
} from '../summary/entities-summary';
import { SummaryItem } from '../summary/summary-item';
import { DownloadEntitiesDataService } from './dowload-entities-data.service';
import {
  NameModeReferenceColumn,
  collectReferenceUidsByType,
  selectNameModeReferenceColumns
} from './entity-reference-names.util';
import { EntityRegistryService } from './entity-registry.service';
import {
  buildAddToPlanModal,
  buildCurrentCsvEntityModeNames,
  buildCurrentEntityTransformers,
  buildDeleteEntitiesModal,
  buildEditionTransformerSchema,
  buildSearchConfig,
  buildUploadZoneConfig
} from './entity-signals-builder';
import { buildTableConfig } from './table.config';
import { buildToolboxSections } from './toolbox.config';
import { UploadEntitiesFileService } from './upload-entities-file.service';

interface PaginationState {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
}

interface DataSummary<T> {
  all: EntitySummary<T> | null;
  selected: EntitySummary<T> | null;
  signals: {
    all: WritableSignal<SummaryItem<T>[]>;
    selected: WritableSignal<SummaryItem<T>[]>;
    allStatus: WritableSignal<SummaryStatus>;
  };
}

interface RouteBatchToasts {
  successTitle: string;
  successMessage: string;
  successParams: Record<string, unknown>;
  errorTitle: string;
  errorMessage: string;
}

/** A bulk write reaching a validated route, refused before its first chunk is sent. */
class ValidatedRouteInTargetsError extends Error {}

const ROUTE_VALIDATION_STATE_KEYS: Record<RouteValidationState, string> = {
  Validated: 'features.data.toolbox.validation_state_validated',
  Unvalidated: 'features.data.toolbox.validation_state_unvalidated'
};

type SelectionBanner =
  | { kind: 'page-selected'; total: number; pageCount: number }
  | { kind: 'all-selected'; total: number; overCap: boolean };

/** The flavour whose rows a session can open and remove. */
const SESSION_ROUTE_FLAVOUR = 'UserRoute';

function sameUids(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((uid, i) => uid === b[i]);
}

/** `rows` itself when none of `uids` is among them, so signals reading it see no change. */
function withoutUids<E>(rows: Map<string, E>, uids: ReadonlySet<string>): Map<string, E> {
  if (![...uids].some((uid) => rows.has(uid))) return rows;
  return new Map([...rows].filter(([uid]) => !uids.has(uid)));
}

const INITIAL_PAG_STATE: PaginationState = {
  currentPage: 1,
  itemsPerPage: 100,
  totalItems: 0
};

/** Keeps detail tab-strip ids unique when several entity views share the page. */
let nextEntityViewId = 0;

@Component({
  selector: 'app-entity',
  imports: [
    PlanEditBannerComponent,
    PlanEditBarComponent,
    DataVisuComponent,
    TableComponent,
    ToolboxComponent,
    FilterQueryBuilderComponent,
    UploadZoneComponent,
    ModalComponent,
    TranslatePipe,
    EntityDetailsComponent,
    EntityDetailTabsComponent,
    SplitComponent,
    SplitAreaComponent,
    ReferencePopupOutletComponent,
    FilterPresetsComponent,
    FilterTemplateAuthorComponent,

    PathFilterSearchBarComponent,
    PathFilterPillsComponent,
    SortTabComponent,
    PathFilterPanelComponent,
    PathFilterComposerComponent,
    PathFilterMoveButtonsComponent,
    FocusFirstDirective,
    ReturnFocusDirective
  ],
  providers: [
    EntityTableService,
    PagedEntityService,
    DownloadEntitiesDataService,
    UploadEntitiesFileService,
    EntityRegistryService,
    FilterPresetsStore,
    PathFilterBarState
  ],
  templateUrl: './entity.component.html',
  styles: ``
})
export class EntityComponent<T> extends AutoUnsubscribe implements OnInit, OnDestroy {
  readonly translate = inject(TranslateService);
  readonly entityLabelsTranslationService = inject(EntityLabelsTranslationService);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly router = inject(Router);

  private readonly entityTableService = inject(EntityTableService);
  private readonly pagedEntityService = inject(PagedEntityService);
  private readonly selectorsService = inject(SelectorsService);
  private readonly splitColumnsService = inject(SplitColumnsService);
  private readonly genericEntityRegistry = inject(GenericEntityRegistryService);
  private readonly hydrationService = inject(EntityHydrationService);
  private readonly userModelsService = inject(UserModelsRegistryService);
  private readonly entityModificationService = inject(EntityModificationService);
  private readonly store = inject(Store);
  private readonly notificationService = inject(NotificationService);
  private readonly downloadService = inject(DownloadEntitiesDataService);
  private readonly uploadService = inject(UploadEntitiesFileService);
  private readonly currentLangService = inject(CurrentLangService);
  private readonly entityRegisterService = inject(EntityRegistryService);
  private readonly csvTransformersService = inject(CsvTransformerService);
  private readonly navService = inject(PlanAwareNavigationService);
  private readonly filterHandoff = inject(EntityListFilterHandoffService);
  private readonly actions$ = inject(Actions);
  private readonly referenceNamesService = inject(ReferenceNamesService);
  private readonly assistantTableState = inject(AssistantTableStateService);

  private dataspace = this.store.selectSignal(selectDataspaceState);
  private plan = this.store.selectSignal(selectPlan);
  private readonly planWatch = this.store.selectSignal(selectPlanWatchForView);
  protected readonly pendingRouteUids = this.store.selectSignal(selectPendingRouteUids);
  private readonly watchOverlay = computed(() => this.planWatch().overlay);
  private readonly watchedPlanId = computed(() => this.planWatch().planId);
  /** An array kept while it is unchanged, so watch changes elsewhere rebuild nothing. */
  private readonly watchLiveFlavours = computed(() => liveFlavours(this.planWatch()));
  private readonly editSession = this.store.selectSignal(selectPlanEditSession);
  readonly planHeld = this.store.selectSignal(selectIsPlanInViewHeld);
  private readonly editSessionPlanId = computed(() => this.editSession().planId);
  private readonly sessionOpenedRouteUids = this.store.selectSignal(selectOpenedRouteUids, {
    equal: sameUids
  });
  private readonly sessionHiddenRouteUids = this.store.selectSignal(selectHiddenRouteUids, {
    equal: (a, b) => a.size === b.size && [...a].every((uid) => b.has(uid))
  });
  /** The routes this tab's session opened, while this view lists the routes of its plan. */
  private readonly openedRoutesInView = computed(
    () =>
      this.liveVersionName() === SESSION_ROUTE_FLAVOUR &&
      this.editSessionPlanId() === this.watchedPlanId()
        ? this.sessionOpenedRouteUids()
        : null,
    { equal: (a, b) => (a === null || b === null ? a === b : sameUids(a, b)) }
  );
  /** The opened routes the selection last followed, so it adds and drops only what changed. */
  private selectionOpenedRouteUids: readonly string[] = [];
  /** The opened routes the session last saved, which outlive it; read before it resets. */
  private savedOpenedRouteUids: readonly string[] = [];
  /** Wrapped overlaid raws, so a patch re-wraps only the entities it replaced. */
  private readonly overlaidEntities = new WeakMap<object, EntityInterface<T>>();
  /**
   * The plan a save replaced, from the save until the refetch it triggers is issued. That refetch
   * keeps it and releases the plan's overlay once its rows land, so the stored plan never flashes.
   */
  private overlayAwaitingRefetch: string | null = null;
  /**
   * The page fetch in flight, and the overlay it releases. A newer fetch cancels it: answering
   * after the newer one, it would put the rows of an outdated query on screen, such as the data
   * space's own rows fetched before the plan in the URL had loaded.
   */
  private pageFetch: { subscription: Subscription; releasing: string | null } | null = null;
  protected readonly planIdInView = computed(() => this.plan()?.id ?? null);
  private readonly paramsService = inject(ParamsService);
  /**
   * The parameters a planner can edit a plan that records none with, as a copy does: its
   * solver's. `null` until read, and for a plan recording its own, which is never asked.
   */
  readonly planEditParameters = toSignal(
    toObservable(
      computed(() => {
        const plan = this.plan();
        return plan && isNil(plan.parameter?.id) && this.entityType() === 'route'
          ? (plan.solver?.id ?? null)
          : null;
      })
    ).pipe(
      distinctUntilChanged(),
      switchMap((solverId) =>
        isNil(solverId)
          ? of(null)
          : this.paramsService.getParamsBySolverId$(solverId).pipe(
              map((list) => list.result),
              startWith(null),
              catchError(() => of(null))
            )
      )
    ),
    { initialValue: null }
  );
  private readonly storedTables = this.store.selectSignal(selectTables);
  private availableLabels = this.store.selectSignal(selectLabels);
  private availableTags = this.store.selectSignal(selectTags);
  private routeParams = toSignal(this.activatedRoute.paramMap);
  /**
   * Whether the bulk actions stand for every row matching the current fetch rather than the
   * ticked ones. It never outlives the selection it describes — clearing or hand-editing the
   * ticks lowers it — because the row set it points at is only the one the fetch just returned.
   */
  private isSelectAllActive = signal(false);
  private lastFetchRequest: EntitySelectorRequestModel | null = null;
  /** The query behind the rows on screen, for the assistant, keyed by the GBE type it queried. */
  private readonly shownQuery = signal<
    (AssistantTableQuery & { entityType: string }) | null
  >(null);
  /**
   * A bulk write spans several page fetches and several batch requests, so the action is held
   * shut while one runs rather than letting a second pick race it over the same rows.
   */
  readonly bulkWriteInFlight = signal(false);

  @ViewChild(ToolboxComponent) toolbox!: ToolboxComponent;

  private dataSummary: DataSummary<T> = {
    all: null,
    selected: null,
    signals: {
      all: signal<SummaryItem<T>[]>([]),
      selected: signal<SummaryItem<T>[]>([]),
      allStatus: signal<SummaryStatus>('idle')
    }
  };
  protected summaryItems = signal<SummaryItem<T>[]>([]);
  private summaryColumnsKey: string | null = null;

  readonly selectionBanner = computed<SelectionBanner | null>(() => {
    const total = this.paginationState().totalItems;

    if (this.isSelectAllActive()) {
      return {
        kind: 'all-selected',
        total,
        overCap: total > SELECT_ALL_SUMMARY_MAX_ROWS
      };
    }

    const pageUids = Array.from(this.fetchedEntitiesInPage().keys());
    const selected = this.selectedItemIds();
    const pageFullySelected =
      pageUids.length > 0 && pageUids.every((uid) => selected.has(uid));
    const moreRowsMatch = total > selected.size;

    if (pageFullySelected && moreRowsMatch) {
      return { kind: 'page-selected', total, pageCount: pageUids.length };
    }

    return null;
  });
  protected readonly selectAllSummaryMaxRows = SELECT_ALL_SUMMARY_MAX_ROWS;
  protected searchPlaceholder = computed(() => {
    this.currentLang(); // Trigger reactivity when language changes
    return this.translate.instant('common.search_placeholder');
  });

  protected selectedItems = computed(() => {
    const ids = this.selectedItemIds();
    if (ids.size === 0) return [];

    const entitiesInPage = this.pageEntities();
    const allEntities = this.knownEntities();
    const result: EntityInterface<T>[] = [];
    const addedUids = new Set<string>();

    // Entities on current page in sort order (matches table display)
    for (const [uid, entity] of entitiesInPage) {
      if (ids.has(uid)) {
        result.push(entity);
        addedUids.add(uid);
      }
    }

    // Selected entities from other pages (insertion order fallback)
    for (const [uid, entity] of allEntities) {
      if (ids.has(uid) && !addedUids.has(uid)) {
        result.push(entity);
      }
    }

    return result;
  });
  protected selectedItemsSet = computed(() => {
    return new Set(this.selectedItems());
  });
  /**
   * A dropped CSV navigates to the upload page relative to the current route. The edition
   * context is the import-from-global picker, which is rendered inside a modal on the plan
   * overview route, where no `upload` child exists — so capturing a file there would strand
   * the user rather than import anything. An entity without a CSV transformer has nothing a
   * dropped file could be imported through, so its drop zone would offer an empty choice.
   */
  protected canUpload = computed(() => {
    if (this.previewOnly()) return false;
    if (this.contextType() === 'edition') return false;
    if (this.currentEntityTransformers().length === 0) return false;
    const plan = this.plan();
    return isNil(plan) || (!isPlanEditBlocked(plan) && !this.planHeld());
  });

  /**
   * How many rows a bulk action would write to. Under the select-all banner that is every
   * matching row, of which the table holds only the page it has fetched.
   */
  readonly bulkTargetCount = computed(() =>
    this.isSelectAllActive()
      ? this.paginationState().totalItems
      : this.selectedItemIds().size
  );

  /**
   * The lock every selected route already carries, or null when the selection is empty or
   * mixed. Only meaningful on the route entity, the one place the batch lock action shows.
   */
  readonly selectedRoutesLockCfg = computed<RouteLockCfg | null>(() => {
    const selected = this.selectedItems();
    if (selected.length === 0) return null;
    // Under the select-all banner the rows in hand are one page of the matching set, and their
    // agreement says nothing about the rows still unfetched.
    if (selected.length < this.bulkTargetCount()) return null;

    const lockOf = (item: EntityInterface<T>): RouteLockCfg | null =>
      (item.raw as { lock_cfg?: RouteLockCfg }).lock_cfg ?? null;

    const first = lockOf(selected[0]);
    return selected.every((item) => lockOf(item) === first) ? first : null;
  });

  /** Same rules as {@link selectedRoutesLockCfg}; a row without the field is `Unvalidated`. */
  readonly selectedRoutesValidationState = computed<RouteValidationState | null>(() => {
    const selected = this.selectedItems();
    if (selected.length === 0) return null;
    if (selected.length < this.bulkTargetCount()) return null;

    const stateOf = (item: EntityInterface<T>): RouteValidationState =>
      isRouteValidated(item.raw as { validation_state?: RouteValidationState })
        ? 'Validated'
        : 'Unvalidated';

    const first = stateOf(selected[0]);
    return selected.every((item) => stateOf(item) === first) ? first : null;
  });

  /** Only the loaded rows are inspected: the rest of a select-all set is not in hand. */
  readonly selectionHasValidatedRoute = computed(() =>
    this.selectedItems().some((item) =>
      isRouteValidated(item.raw as { validation_state?: RouteValidationState })
    )
  );

  readonly selectionValidationBlocker = computed<string | null>(() => {
    for (const item of this.selectedItems()) {
      const blocker = routeValidationBlocker(item.raw as { is_template?: boolean });
      if (blocker !== null) return blocker;
    }
    return null;
  });

  readonly selectedItemIds = signal<Set<string>>(new Set<string>());
  protected nbSelectedItems = computed(() => this.selectedItemIds().size);
  protected fetchedEntitiesInPage = signal<Map<string, EntityInterface<T>>>(new Map());
  protected allFetchedEntities = signal<Map<string, EntityInterface<T>>>(new Map());
  protected readonly pageEntities = computed(() =>
    this.withWatchOverlay(this.fetchedEntitiesInPage())
  );
  private readonly knownEntities = computed(() =>
    this.withWatchOverlay(this.allFetchedEntities())
  );

  protected entitiesArrayInPage = computed(() => Array.from(this.pageEntities().values()));

  protected onlySelected = signal(false);
  protected tableEntities = computed(() =>
    this.onlySelected() ? this.selectedItems() : this.entitiesArrayInPage()
  );

  protected searchResult = signal<SearchResult | null>(null);
  protected advancedFilters = signal<FilterConfiguration | null>(null);
  protected openFilterForField = signal<string | null>(null);
  protected showAdvancedFiltering = signal<boolean>(false);
  protected searchQuery = signal<string>('');
  protected shouldFocus = signal(false);

  /** The detail pane's sides, left then right; each shows its active tab. */
  protected detailGroups = signal<DetailGroup[]>([]);
  protected detailPanes = computed(() =>
    this.detailGroups().map((group, index, groups) => ({
      uids: group.tabs.map((t) => t.entity.uid),
      activeUid: group.activeUid,
      active: activeDetailOf(group),
      // A lone tab on a lone side has nowhere to split to without emptying the left.
      splitTarget:
        index === 1
          ? ('left' as const)
          : groups.length > 1
            ? ('right' as const)
            : group.tabs.length > 1
              ? ('split' as const)
              : null
    }))
  );
  /** Side the row menu opens new details into: the one the user last worked in. */
  private focusedDetailGroup = signal(0);
  private readonly detailListPrefix = `entity-detail-tabs-${nextEntityViewId++}`;
  protected detailListIds = computed(() =>
    this.detailGroups().map((_, i) => `${this.detailListPrefix}-${i}`)
  );
  private detailsHost = viewChild<ElementRef<HTMLElement>>('detailsHost');
  /**
   * Pixel height of the detail pane, so resizing the visu below only resizes the main table.
   * Measured once when the pane opens; afterwards only the user's drag changes it.
   */
  protected detailsPaneHeight = signal(0);
  readonly exportState = this.downloadService.exportState;
  /** The Classic header sort; in Path the header is a view of the Sort tab's list. */
  private sortState = signal<SortState<EntityInterface<T>>[]>([]);
  /**
   * The Classic header as a preset, a restore or the user last set it: what the header shows
   * again when the list comes back from Path. A preset seeds the table through this input rather
   * than by writing `sortState`, which keeps the table the single source of the order and works
   * even before it is mounted — it only renders once the entity has rows.
   */
  private classicSort = signal<SortState<EntityInterface<T>>[]>([]);
  /** What the table's header is seeded with; every new array replaces the header's own state. */
  protected presetSort = linkedSignal<SortState<EntityInterface<T>>[]>(() =>
    this.activeGrammar() === 'path'
      ? sortStateFor(this.pathBar.sort(), this.entityTableService.sortableColMap())
      : this.classicSort()
  );
  private readonly availableEntities =
    this.store.selectSignal<EntityMetadata[]>(selectAvailableEntities);
  protected entityName = computed(() => {
    const allEntites = this.availableEntities();
    const entityCfg = this.entityCfg();

    let foundEntity = allEntites.find((e) => e.name === this.entityType());
    if (!foundEntity && entityCfg) {
      foundEntity = allEntites.find((e) =>
        e.versions.some((v) => v.versionName === this.entityVersionName())
      );
    }

    return foundEntity?.displayName?.[
      this.currentLang() as AvailableLanguages
    ].toLowerCase();
  });
  protected paginationState = signal<PaginationState>({ ...INITIAL_PAG_STATE });
  overrideEntityType = input<string | undefined>(undefined);
  readonly entityType = computed(() => {
    return (
      this.overrideEntityType() ||
      this.routeParams()?.get(AppRoutes.data.view.params.entity) ||
      ''
    );
  });
  /** `null` lets the toggle and the saved preference decide; a value forces that grammar and hides the toggle. */
  grammar = input<FilterGrammar | null>(null);
  /** Seeds the Path filter, e.g. with the recipe filter being edited. */
  initialPathFilter = input<FilterExpr | undefined>(undefined);
  /**
   * Applied to the tree of every Path data request, the empty one included, and to nothing else:
   * neither `pathFilterChange` nor the by-filter actions see it. It receives the tree as typed, holes
   * included, and must fill or drop them.
   */
  pathFilterTransform = input<
    ((expr: FilterExpr | undefined) => FilterExpr | undefined) | null
  >(null);
  /** A preview of the rows a filter selects: nothing can be ticked, edited, uploaded or saved as a preset. */
  previewOnly = input(false);
  pathComposerMode = input<ComposerMode>('data');
  pathParameters = input<ParameterOption[]>([]);
  pathFilterChange = output<FilterExpr | undefined>();
  pathNewParameterRequested = output<NewParameterRequest>();

  protected readonly ICONS = ICONS;
  private readonly injector = inject(Injector);
  protected readonly pathBar = inject(PathFilterBarState);
  protected readonly vocabulary = this.pathBar.vocabulary;
  protected readonly activeGrammar = this.pathBar.activeGrammar;
  protected readonly pathFetch = this.pathBar.fetch;
  protected readonly toolboxSearchConfig = this.pathBar.toolboxSearch(() =>
    this.searchCfg()
  );
  protected readonly pills = viewChild(PathFilterPillsComponent);
  /** The entity type the Path state belongs to; the router reuses this component across types. */
  private pathEntityType: string | null = null;

  actions = signal<TableAction<EntityInterface<T>>[]>([]);
  readonly deleteEntitiesModal = buildDeleteEntitiesModal(this, this.getPageContext());
  readonly addToPlanModal = buildAddToPlanModal(this);

  columns = this.entityTableService.columns;

  /** entityType -> (uid -> name), accumulated as pages resolve. */
  protected readonly resolvedReferenceNames = signal<Map<string, Map<string, string>>>(
    new Map()
  );

  /** Lookup bound to the table's `referenceNames` input. */
  protected readonly referenceNameLookup: ReferenceNameLookup = (entityType, uid) =>
    this.resolvedReferenceNames().get(entityType)?.get(uid);

  // Map from column access key to filter cfg
  filterableColMap = computed<Record<string, FilterableColCfg>>(() => {
    this.availableLabels(); // Rebuild when labels load
    const tableCfg = this.entityCfg();
    if (isNil(tableCfg)) {
      return {};
    }
    return makeFilterCfgMap(tableCfg);
  });

  // Available filter fields for complex filter builder
  availableAdvancedFilters = computed<AvailableFilterField[]>(() => {
    this.availableLabels(); // Rebuild when labels load
    const tags = this.availableTags();
    const tableCfg = this.entityCfg();
    if (isNil(tableCfg)) {
      return [];
    }
    return makeAvailableFilterFields(tableCfg, this.entityLabelsTranslationService, tags);
  });

  protected readonly filterPresetsStore = inject(FilterPresetsStore);

  /** The panel's composer accepts `{{name}}` holes only while a template is authored. */
  protected readonly composerMode = computed<ComposerMode>(() =>
    this.filterPresetsStore.templateAuthoring() ? 'template' : this.pathComposerMode()
  );
  protected readonly composerParameters = computed<ParameterOption[]>(() =>
    this.filterPresetsStore.templateAuthoring()
      ? templateParameters(
          this.pathBar.wire(),
          this.filterPresetsStore.templateLabels()
        ).map(({ name, kind, label }) => ({ name, kind, ...(label ? { label } : {}) }))
      : this.pathParameters()
  );
  /** Where the template being authored was started; leaving it ends the template. */
  private authoringContext: string | null = null;

  protected onNewParameterRequested(request: NewParameterRequest): void {
    if (this.filterPresetsStore.templateAuthoring()) {
      this.filterPresetsStore.templateParameterRequest.set(request);
    } else {
      this.pathNewParameterRequested.emit(request);
    }
  }

  /** Ends the template being authored and takes its holes out of the filter: nothing can fill them. */
  private abandonTemplate(): void {
    this.filterPresetsStore.endTemplateAuthoring();
    const tree = this.pathBar.wire();
    if (tree && holdsHoles(tree)) {
      this.pathBar.load(withoutTemplateHoles(tree), this.pathBar.sort());
      this.afterPathEdit();
    }
  }

  protected dataspaceId = computed(() => this.dataspace().dataspace?.id);

  /**
   * Both identities of every sortable column: the DB name a preset stores and the column
   * key the table sorts by. Columns the user has hidden are still listed — a preset saved
   * with a sort on one must keep working after it is hidden.
   */
  protected sortableFieldRefs = computed<SortableFieldRef[]>(() => {
    const sortCfgs = this.entityTableService.sortableColMap();
    const labels = new Map(
      this.columns().map((column) => [String(column.key), column.label])
    );
    return Object.entries(sortCfgs).map(([columnKey, cfg]) => ({
      columnKey,
      dbColName: cfg.dbColName,
      ...(isNil(cfg.labelKey) ? {} : { labelKey: cfg.labelKey }),
      label: labels.get(columnKey) ?? columnKey
    }));
  });

  /**
   * Every target the Sort tab offers: the vocabulary's sorts, then the grid's own column sorts it
   * does not know, which is where KPI sorts live. A label column sorts by a key a Path sort cannot
   * name, so it is left out.
   */
  protected readonly sortTabOptions = computed<SortOption[]>(() => {
    const vocabulary = this.vocabulary();
    if (!vocabulary) return [];
    const lang = this.currentLang() as AvailableLanguages;
    const entity = this.entityTableService.entityDisplayName();
    const known = sortOptions(
      vocabulary,
      (sortable) => sortableLabel(sortable, vocabulary, lang, this.translate),
      (aggregate) =>
        this.translate.instant(`${PATH_FILTER_I18N}.sort.aggregate.${aggregate}`),
      (edge) => {
        const association = edge === null ? undefined : vocabulary.association(edge);
        return association ? associationLabel(association, lang, this.translate) : entity;
      }
    );
    const columns = this.sortableFieldRefs()
      .filter((ref) => isNil(ref.labelKey))
      .map(({ dbColName, label }) => ({
        dbColName,
        label,
        type: vocabulary.field(null, fieldPath(dbColName))?.type ?? null
      }));
    return [...known, ...columnSortOptions(columns, known, entity)];
  });

  /**
   * What saving a preset right now would store. Keyed by translated label because that is
   * what the search bar emits as qualifier keys; the sort is already DB-column-keyed by
   * `getOrderBy`, which is the shape the data request uses.
   */
  protected filterPresetPayload = computed(() => {
    if (this.activeGrammar() === 'path') return this.pathBar.presetPayload();
    const tableCfg = this.entityCfg();
    const sortState = this.sortState();
    // Only a sort the user actually set: with no columns sorted `getOrderBy` answers the
    // entity's default ordering, and offering to save that would make the checkbox
    // permanent and store an order nobody chose.
    const columnSort =
      sortState.length === 0 ? undefined : this.entityTableService.getOrderBy(sortState);
    return buildFilterPresetPayload(
      this.searchResult(),
      this.advancedFilters(),
      isNil(tableCfg)
        ? {}
        : makeTranslatedFilterCfgMap(tableCfg, this.entityLabelsTranslationService),
      columnSort
    );
  });

  searchCfg = buildSearchConfig(this);

  contextType = input<'visualization' | 'edition'>('visualization');
  /** The import picker opens over the page's list, so only the page's list takes `/`. */
  protected readonly ownsSearchShortcut = computed(
    () => this.contextType() === 'visualization' && !this.previewOnly()
  );
  dataAdded = output<{
    entities: T[];
    entityTypeName: string;
    planId: string;
  }>();
  dataAddedByFilter = output<{
    entityType: string;
    planId: string;
    filters?: FilterConfiguration;
    search?: string;
    pathFilter?: FilterExpr;
  }>();
  error = signal<string | null>(null);
  isPopupOpen = this.store.selectSignal(selectIsPopupOpen);
  isUploadZoneOpen = signal(false);
  showPlanToolbox = input<boolean>(false);
  showTableCsvExport = computed(() =>
    ['route', 'route_action'].includes(this.entityType())
  );
  isVisuAllowed = this.store.selectSignal(selectIsPermissionEnabled('visu'));
  isVisuEnabled = signal<boolean>(false);
  private visualizationPreferencesMap = this.store.selectSignal(
    selectVisualizationPreferencesMap
  );
  private userPreferencesLoaded = this.store.selectSignal(selectUserPreferencesLoaded);

  protected readonly tableConfig = computed(() => {
    const config = buildTableConfig(this, this.toggleRowSelection.bind(this));
    return this.previewOnly()
      ? {
          ...config,
          editable: undefined,
          selection: config.selection && { ...config.selection, enabled: false }
        }
      : config;
  });

  private clearSelectedItems(): void {
    this.selectedItemIds.set(new Set());
    this.isSelectAllActive.set(false);
  }

  private toggleRowSelection(row: EntityInterface<T>): void {
    this.selectedItemIds.update((ids) => {
      const newIds = new Set(ids);

      if (newIds.has(row.uid)) newIds.delete(row.uid);
      else newIds.add(row.uid);

      return newIds;
    });
    // Once a row is ticked or unticked by hand the set is no longer "every matching row", so
    // the banner's claim, and the bulk actions reading it, must give way to the ticks.
    this.isSelectAllActive.set(false);
  }

  private getEntities(): void {
    this.fetchEntities(
      this.dataspace(),
      this.searchResult(),
      this.advancedFilters(),
      this.sortState(),
      this.pathFetch()
    );
  }

  /**
   * Refetches once rows were created, edited or deleted. The refetch repeats the request the
   * footer walk last ran, which the walk takes for "nothing changed" and skips, so it is
   * dropped first to make the totals follow the new rows.
   */
  private refetchAfterWrite(): void {
    this.dataSummary.all?.cancel();
    this.getEntities();
  }

  /**
   * The flavour whose rows the watched session's changes are laid over: only the plan's own rows,
   * and only flavours the session projects. The import-from-global picker lists rows the plan does
   * not have. `null` when the overlay does not apply.
   */
  private readonly liveVersionName = computed(() => {
    const watchedPlanId = this.watchedPlanId();
    const versionName = this.entityVersionName();
    if (
      watchedPlanId === null ||
      watchedPlanId !== this.plan()?.id ||
      this.contextType() !== 'visualization' ||
      isNil(versionName) ||
      !this.watchLiveFlavours().includes(versionName)
    ) {
      return null;
    }
    return versionName;
  });

  /**
   * A route the session opens joins the selection, so the timeline draws its row, and leaves it
   * when an undo, a refusal or a removal takes it away, or when the session ends without having
   * saved it. A saved new route stays selected.
   */
  private followOpenedRoutes(
    opened: readonly string[] | null,
    saved: readonly string[]
  ): void {
    const before = this.selectionOpenedRouteUids;
    const kept = this.savedOpenedRouteUids;
    this.selectionOpenedRouteUids = opened ?? [];
    this.savedOpenedRouteUids = opened === null ? [] : saved;
    const added = opened?.filter((uid) => !before.includes(uid)) ?? [];
    const gone =
      opened === null
        ? before.filter((uid) => !kept.includes(uid))
        : before.filter((uid) => !opened.includes(uid));
    if (added.length === 0 && gone.length === 0) return;
    this.selectedItemIds.update((ids) => {
      const next = new Set(ids);
      for (const uid of gone) next.delete(uid);
      for (const uid of added) next.add(uid);
      return next;
    });
  }

  /** Lays the watched session's changes over rows of the plan it edits. */
  private withWatchOverlay(
    rows: Map<string, EntityInterface<T>>
  ): Map<string, EntityInterface<T>> {
    const versionName = this.liveVersionName();
    if (versionName === null) return rows;
    const opened = this.openedRoutesInView();
    const overlaid = overlayEntityMap(
      rows,
      this.watchOverlay(),
      versionName,
      this.genericEntityRegistry.entityTypeFor(versionName),
      (raw) => {
        const cached = this.overlaidEntities.get(raw);
        if (cached) return cached;
        const [wrapped] = this.userModelsService.convertToEntityInterface<T>(
          [raw as T],
          versionName
        );
        if (wrapped) this.overlaidEntities.set(raw, wrapped);
        return wrapped;
      },
      opened ?? []
    );
    return opened === null
      ? overlaid
      : withoutUids(overlaid, this.sessionHiddenRouteUids());
  }

  /** Resolution context (dataspace + plan) for reference-name lookups, or null if unknown. */
  private referenceContext(): ReferenceResolutionContext | null {
    const dataSpaceId = this.dataspace().dataspace?.id;
    if (isNil(dataSpaceId)) return null;
    const planId = this.plan()?.id;
    return isNil(planId) ? { dataSpaceId } : { dataSpaceId, planId };
  }

  /**
   * Reference columns currently set to name mode, with their target entity type.
   *
   * The rule (reference column + `uid`/`uid-list` + persisted `displayMode` of
   * `'name'`) lives in `selectNameModeReferenceColumns` so it has one documented,
   * independently tested home; see `entity-reference-names.util.ts`.
   */
  private nameModeReferenceColumns(): NameModeReferenceColumn<EntityInterface<T>>[] {
    return selectNameModeReferenceColumns(
      this.columns() as TableColumn<EntityInterface<T>>[],
      this.storedTables()?.[this.entityType()]
    );
  }

  private fetchEntities(
    dataspace: DataspaceState,
    searchResult: SearchResult | null,
    filterConfiguration: FilterConfiguration | null,
    sortState: SortState<EntityInterface<T>>[],
    path: PathFetch | null
  ) {
    // The grammar is not known yet: a request now would be Classic, then redone in Path.
    if (this.pathBar.fetchHeld()) return;

    const versionName = this.entityVersionName();
    if (isNil(versionName)) {
      this.overlayAwaitingRefetch = null;
      this.fetchedEntitiesInPage.set(new Map());
      this.resetPagination();
      return;
    }

    if (isNil(dataspace.dataspace?.id)) {
      this.overlayAwaitingRefetch = null;
      console.error(
        'incoherent state in entity component: trying to fetch entities but dataspace id is undefined'
      );
      return;
    }

    const planId = this.plan()?.id;
    const plan = isNil(planId)
      ? null
      : {
          id: planId,
          context: this.contextType()!
        };

    const transform = this.pathFilterTransform();
    const sentFilter = path && transform ? transform(path.filter) : path?.filter;
    // Path with no sort at all falls back to the entity's default order, as Classic does.
    const columnOrder = this.entityTableService.getOrderBy(path ? [] : sortState);
    const request = this.pagedEntityService.fetchEntities<T>({
      entityType: this.entityType(),
      entityVersionName: this.entityVersionName()!,
      dataSpaceId: dataspace.dataspace?.id,
      plan: plan ?? undefined,
      orderBy: path
        ? pathOrderBy(path.sort, path.sort.length > 0 ? undefined : columnOrder)
        : columnOrder,
      searchResult: path ? undefined : (searchResult ?? undefined),
      filterConfiguration: path ? undefined : (filterConfiguration ?? undefined),
      ...(path ? { path: { filter: sentFilter, search: path.search } } : {})
    });
    this.processFetchEntitiesResult(request);
  }

  private processFetchEntitiesResult(
    result: Observable<PagedList<EntityInterface<T>>>
  ): void {
    const request = this.pagedEntityService.getLastRequest()!;
    this.lastFetchRequest = request;
    const superseded = this.pageFetch;
    superseded?.subscription.unsubscribe();
    // The cancelled fetch's release is this one's: these rows are as new as the ones it awaited.
    const releasing = this.overlayAwaitingRefetch ?? superseded?.releasing ?? null;
    this.overlayAwaitingRefetch = null;
    const pageFetch = { subscription: Subscription.EMPTY, releasing };
    this.pageFetch = pageFetch;
    pageFetch.subscription = result.subscribe({
      next: (res) => {
        if (this.pageFetch === pageFetch) this.pageFetch = null;
        this.fetchedEntitiesInPage.update(() => {
          const newMap = new Map();
          res.result.forEach((entity) => {
            newMap.set(entity.uid, entity);
          });
          return newMap;
        });
        this.paginationState.set({
          ...this.paginationState(),
          itemsPerPage: Math.abs(res.take),
          totalItems: res.total
        });
        if (request) this.recordShownQuery(request, res.total);
        this.refreshFullSummary(request);
        this.pathBar.error.set(null);
        if (releasing !== null) {
          this.store.dispatch(PlanWatchActions.overlayReleased({ planId: releasing }));
        }
      },
      // The rows of the last successful request stay on screen, and the release waits for the
      // next fetch, which inherits it from this one.
      error: (error: unknown) =>
        this.pathBar.reportError(error, this.translate.instant('common.error'))
    });
  }

  private recordShownQuery(request: EntitySelectorRequestModel, total: number): void {
    // The assistant's view has no Path tree, and gbe-mcp resolves a select-all through that view:
    // a search-only view would widen it to every row matching the text. Without a query, only
    // explicitly ticked rows reach the assistant.
    if (request.pathFilter !== undefined) {
      this.shownQuery.set(null);
      return;
    }
    const filters = convertFilterConfigurationToBackEndFormat(request.filters);
    this.shownQuery.set({
      entityType: request.entityType,
      ...(request.search ? { search: request.search } : {}),
      // As SelectorsService and the request interceptor put it on the wire: AssistantApiService
      // posts with fetch, which no interceptor sees.
      ...(filters ? { filters: convertKeysToSnakeCase(filters) } : {}),
      total
    });
  }

  /**
   * Starts (or restarts) the footer-summary walk, donating the page already in memory so the
   * totals are computed without re-requesting those rows. Signals are read untracked because
   * one caller is an effect that must not re-run on every page load.
   */
  private refreshFullSummary(request: EntitySelectorRequestModel): void {
    if (isNil(this.dataSummary.all)) {
      return;
    }
    const pagination = untracked(() => this.paginationState());
    if (pagination.totalItems > PASSIVE_SUMMARY_MAX_ROWS) {
      this.dataSummary.all.cancel();
      return;
    }
    this.dataSummary.all.updateRequest(request, PASSIVE_SUMMARY_MAX_ROWS, {
      rows: Array.from(untracked(() => this.fetchedEntitiesInPage()).values()),
      page: pagination.currentPage,
      pageSize: pagination.itemsPerPage,
      total: pagination.totalItems
    });
  }

  protected paginationConfig = computed(() =>
    Pagination.createPaginationConfig(this.paginationState())
  );

  readonly allTransformers = this.store.selectSignal(selectAvailableTransformers);
  readonly editionTransformerSchema = buildEditionTransformerSchema(this);
  readonly currentEntityTransformers = buildCurrentEntityTransformers(this);
  readonly currentCsvEntityModelNames = buildCurrentCsvEntityModeNames(this);
  readonly entityVersionName = this.entityTableService.entityVersionName;
  readonly entityCfg = this.entityTableService.entityCfg;
  readonly availableDetailEntityTypes = this.entityTableService.availableDetailEntityTypes;
  readonly currentLang = this.currentLangService.currentLang;
  private currentDataSpaceId: number | null = null;
  private currentPlanId: string | undefined = undefined;
  hasSelectionSignal = computed(() => this.selectedItemIds().size > 0);

  protected customTransformers = toSignal(
    combineLatest([toObservable(this.dataspace), toObservable(this.entityType)]).pipe(
      switchMap(([{ dataspace }, entityType]) => {
        if (isNil(dataspace)) {
          return of([]);
        }
        return this.csvTransformersService.getCustomTransformers(dataspace.id, entityType);
      })
    ),
    { initialValue: [] as CustomTransformerResponse[] }
  );

  readonly toolboxSections = buildToolboxSections(
    this,
    this.store,
    this.customTransformers
  );
  uploadZoneConfig = buildUploadZoneConfig(this);

  // Debounces sort, filter and search edits into one request. The fetch hold and the host
  // transform are read by `fetchEntities` itself; they are here only so that releasing the
  // hold or handing a new transform re-queries.
  private readonly onUpdateSortOrSearch$ = toObservable(
    computed(() => ({
      sortState: this.sortState(),
      searchResult: this.searchResult(),
      filterConfiguration: this.advancedFilters(),
      path: this.pathFetch(),
      held: this.pathBar.fetchHeld(),
      transform: this.pathFilterTransform()
    }))
  );

  startPlanEdit(paramId: number): void {
    const planId = this.planIdInView();
    if (isNil(planId)) return;
    openPlanEdit({
      planId,
      paramId,
      popupOpen: this.isPopupOpen(),
      dispatch: (action) => this.store.dispatch(action),
      showVisu: () => this.isVisuEnabled.set(true)
    });
  }

  downloadData(type: 'json' | 'csv', modelName?: string) {
    this.downloadService.downloadData(
      this.entityType(),
      type,
      modelName,
      this.lastFetchRequest!,
      this.availableDetailEntityTypes()
    );
  }

  downloadTableDisplayedData(): void {
    this.downloadService.downloadDisplayedTableData(
      this.columns(),
      this.entityType(),
      this.lastFetchRequest!,
      this.availableDetailEntityTypes()
    );
  }

  constructor() {
    super();

    effect((onCleanup) => {
      if (!this.canUpload()) return;
      onCleanup(this.setupFileDropListeners());
    });

    // The preset store lives here rather than in the preset UI: that UI sits inside the
    // advanced filter bar, which is only rendered once filtering is open, so a store
    // owned by it could never load a default preset and open that bar to begin with.
    effect(() => {
      this.filterPresetsStore.entityType.set(this.entityType());
      this.filterPresetsStore.dataSpaceId.set(this.dataspaceId());
      this.filterPresetsStore.availableFields.set(this.availableAdvancedFilters());
      this.filterPresetsStore.sortableFields.set(this.sortableFieldRefs());
      this.filterPresetsStore.currentPayload.set(this.filterPresetPayload());
    });

    this.filterPresetsStore.applied
      .pipe(takeUntilDestroyed())
      .subscribe((applied) => this.onFilterPresetApplied(applied));

    effect(() => {
      const opened = this.openedRoutesInView();
      const saved = this.editSession().routesOpenedAtSave;
      untracked(() => this.followOpenedRoutes(opened, saved));
    });

    effect(() => {
      const entitiesInPage = this.fetchedEntitiesInPage();
      const currentEntities = untracked(() => this.allFetchedEntities());

      // Check if there are any actual changes
      let hasChanges = false;
      const entitiesToAdd: EntityInterface<T>[] = [];

      Array.from(entitiesInPage.values()).forEach((entity) => {
        const existingEntity = currentEntities.get(entity.uid);
        if (!existingEntity || existingEntity !== entity) {
          hasChanges = true;
          entitiesToAdd.push(entity);
        }
      });

      // Only update if there are actual changes
      if (hasChanges && entitiesToAdd.length > 0) {
        this.allFetchedEntities.update((currentEntities) => {
          const newMap = new Map(currentEntities);
          entitiesToAdd.forEach((entity) => {
            newMap.set(entity.uid, entity);
          });
          return newMap;
        });
      }
    });
    effect(() => {
      // A job view's selection stays put when only a route it shows is patched; the build reads
      // the overlaid routes from the aux bundle, so any overlay change has to rebuild.
      this.watchOverlay();
      const entityCfg = this.entityCfg();
      const dataSpaceId = this.dataspace().dataspace?.id;
      if (!isNil(entityCfg)) {
        this.store.dispatch(
          setVisualizedEntities({
            entityCfg: entityCfg as EntityCfg<unknown>,
            entities: this.selectedItems() as EntityInterface<unknown>[],
            auxContext: isNil(dataSpaceId)
              ? undefined
              : {
                  dataSpaceId,
                  planId: this.plan()?.id,
                  planOperator: isNil(this.plan()?.id)
                    ? undefined
                    : this.contextType() === 'visualization'
                      ? PlanFilterOperator.IN
                      : PlanFilterOperator.NOT_IN
                }
          })
        );
      }
    });
    // Seed the visualization defaults per entity-view mount (keyed on
    // `entityCfg`, stable across selection changes) and re-seed once the async
    // preferences finish loading — otherwise a view that mounts before the load
    // completes would keep the resolved-from-empty defaults (e.g. map open) even
    // for a user who prefers table-only. Preferences themselves are read
    // `untracked` so editing them mid-session never re-runs this effect and
    // clobbers the user's current manual eye/window toggles.
    effect(() => {
      const entityCfg = this.entityCfg();
      this.userPreferencesLoaded();
      if (isNil(entityCfg)) return;

      const entityType = untracked(() => this.entityType());
      const settings = untracked(() => this.visualizationPreferencesMap())[entityType];
      const { enabled, windows } = resolveVisualizationDefaults(
        settings,
        entityCfg.supportedVisualizations()
      );

      // (1) master show/hide toggle default
      this.isVisuEnabled.set(enabled);

      // (2) default open windows — only for modules this entity actually supports
      this.store.dispatch(toggleMapVisualization({ isOpen: windows.map }));
      this.store.dispatch(toggleTimelineVisualization({ isOpen: windows.timeline }));
      this.store.dispatch(toggleCalendarVisualization({ isOpen: windows.calendar }));
      this.store.dispatch(toggleListVisualization({ isOpen: windows.list }));
    });
    effect(() => {
      // The data space is isolated here because some action is triggered when when a request is sent making this effect to be executed
      const dataspace = this.dataspace();
      const plan = this.plan();

      // Check if neither dataspace nor plan changed
      if (
        isNil(dataspace.dataspace?.id) ||
        (this.currentDataSpaceId === dataspace.dataspace?.id &&
          this.currentPlanId === plan?.id)
      ) {
        return;
      }

      this.currentDataSpaceId = dataspace.dataspace?.id;
      this.currentPlanId = plan?.id;
      const searchResult = untracked(() => this.searchResult());
      const sortState = untracked(() => this.sortState());
      const filterConfiguration = untracked(() => this.advancedFilters());
      const path = untracked(() => this.pathFetch());
      this.fetchEntities(dataspace, searchResult, filterConfiguration, sortState, path);
    });
    effect(() => {
      const versionName = this.entityVersionName();
      const dataspaceId = this.dataspace().dataspace?.id;
      this.plan();

      if (isNil(versionName) || isNil(dataspaceId)) {
        return;
      }

      this.splitColumnsService.load(versionName, this.contextType(), this.selectorsService);
      const splitTargets = this.genericEntityRegistry.getSplitColumnTargets(versionName);
      if (splitTargets.length > 0) {
        this.splitColumnsService.loadTargets(
          versionName,
          splitTargets,
          this.contextType(),
          this.selectorsService
        );
      }
    });
    // Init summaries
    effect(() => {
      const columns = this.columns();
      if (columns.length === 0) {
        return;
      }

      const summaryKey = columns
        .filter(
          (col) => ((col as { summaryTypes?: unknown[] }).summaryTypes ?? []).length > 0
        )
        .map((col) => String(col.key))
        .join(',');
      const summaryColumnsChanged = summaryKey !== this.summaryColumnsKey;

      if (summaryColumnsChanged) {
        this.summaryColumnsKey = summaryKey;
        this.dataSummary.selected = EntitySummary.createStaticSummary<T>(
          columns,
          this.dataSummary.signals.selected
        );
        const selected = untracked(() => this.selectedItems());
        if (selected.length > 0) {
          this.dataSummary.selected.updateStaticData(selected);
        }
      }

      if ((isNil(this.dataSummary?.all) || summaryColumnsChanged) && columns.length > 0) {
        this.dataSummary.all = EntitySummary.createLazySummary<T>(
          columns,
          this.dataSummary.signals.all,
          this.selectorsService,
          this.dataSummary.signals.allStatus
        );
        if (!isNil(this.lastFetchRequest)) {
          this.refreshFullSummary(this.lastFetchRequest);
        }
      }
    });
    // Effect 1: update static summary data when selected items change
    effect(() => {
      const selectedItems = this.selectedItems();
      if (
        !this.isSelectAllActive() &&
        selectedItems.length > 0 &&
        !isNil(this.dataSummary?.selected)
      ) {
        this.dataSummary.selected.updateStaticData(selectedItems);
        return;
      }
      // Selection totals that outlive the selection are the footer's only other candidate, so
      // effect 2 republishes them on the next full-set walk — for rows nobody has ticked any
      // more, and which the filter that cleared the ticks may well exclude.
      this.dataSummary.signals.selected.set([]);
      if (!isNil(this.dataSummary.all) && this.dataSummary.all.isLazyDataLoadaded) {
        // set back the complete summary
        this.summaryItems.set(this.dataSummary.all.getSummaryItems());
      } else {
        this.summaryItems.set([]);
      }
    });
    // Effect 2: update displayed summary items when summary data signals change
    effect(() => {
      const completeSummary = this.dataSummary.signals.all();
      const selectedSummary = this.dataSummary.signals.selected();

      if (this.isSelectAllActive()) {
        // Numbers must track the full-set walk, never the page-scoped selected summary.
        this.summaryItems.set(
          this.dataSummary.all?.isLazyDataLoadaded ? completeSummary : []
        );
        return;
      }

      if (completeSummary.length === 0 && selectedSummary.length === 0) {
        this.summaryItems.set([]);
      }
      if (selectedSummary.length > 0) {
        this.summaryItems.set(selectedSummary);
      } else if (this.dataSummary?.all?.isLazyDataLoadaded) {
        this.summaryItems.set(this.dataSummary.all.getSummaryItems());
      }
    });
    effect(() => this.pathBar.entityType.set(this.entityType()));
    effect(() => this.pathBar.fillsHoles.set(this.pathFilterTransform() !== null));
    effect(() => this.pathBar.forcedGrammar.set(this.grammar()));
    this.pathBar.onEdit(() => this.afterPathEdit());
    this.pathBar.onBarEdit(() => {
      this.resetPagination();
      this.clearSelectedItems();
    });
    this.pathBar.classicFiltered.set(() => {
      const { filters, search } = this.filterPresetPayload();
      return !isNil(filters) || !isNil(search);
    });
    effect(() => this.filterPresetsStore.pathOnly.set(this.grammar() === 'path'));
    effect(() => {
      const initial = this.initialPathFilter();
      if (initial !== undefined) untracked(() => this.pathBar.load(initial, []));
    });
    // Only the panel's composer offers the Value | Parameter switch; the pills never do.
    effect(() => {
      if (this.pathComposerMode() !== 'data')
        untracked(() => this.pathBar.pathPanel.set('filter'));
    });
    // Presets load after the mount; the default one must not replace a seeded filter, and a preview has none.
    effect(() => {
      if (this.initialPathFilter() !== undefined || this.previewOnly()) {
        untracked(() => this.filterPresetsStore.suppressDefaultPreset());
      }
    });
    effect(() => {
      if (this.activeGrammar() === 'path') this.pathFilterChange.emit(this.pathBar.wire());
    });
    // A Classic bar cannot apply a template's tree, so it does not list templates.
    effect(() =>
      this.filterPresetsStore.showsTemplates.set(this.activeGrammar() === 'path')
    );
    // A template is authored in the open Path panel of one entity type. Leaving that context any
    // other way than Save or Discard would keep the composer in template mode, keep the classic
    // "Save current filters" disabled, and leave holes in a filter nothing can fill any more.
    effect(() => {
      const authoring = this.filterPresetsStore.templateAuthoring();
      const panelOpen = this.pathBar.pathPanel() !== null;
      const context = `${this.entityType()}|${this.activeGrammar()}|${panelOpen}`;
      untracked(() => {
        if (!authoring) {
          this.authoringContext = null;
        } else if (this.authoringContext === null) {
          this.authoringContext = context;
        } else if (this.authoringContext !== context) {
          this.abandonTemplate();
        }
      });
    });
    // init entityTableService with the current entityType
    effect(() => {
      const entityType = this.entityType();
      if (isNil(entityType) || entityType === '') {
        return;
      }
      this.entityTableService.setEntityType(entityType);
      this.allFetchedEntities.set(new Map()); // reset accumulated cross-page cache
      // The router reuses this component across `data/:entity` params, so ticks made on the
      // previous entity's rows would otherwise survive into this one.
      this.clearSelectedItems();
      // A filter written for the previous entity names its fields, not this one's.
      if (this.pathEntityType !== null && this.pathEntityType !== entityType) {
        this.pathBar.reset();
        this.pathBar.presetGrammar.set(null);
      }
      this.pathEntityType = entityType;
    });
    effect(() => {
      const entityType = this.entityType().toUpperCase();
      const shown = this.shownQuery();
      if (entityType === '') {
        this.assistantTableState.state.set(null);
        return;
      }
      // Until the new type's rows land, the last query is the previous type's.
      const query =
        shown?.entityType === entityType
          ? { search: shown.search, filters: shown.filters, total: shown.total }
          : undefined;
      this.assistantTableState.state.set({
        entityType,
        uids: [...this.selectedItemIds()],
        allMatching: this.isSelectAllActive(),
        ...(query ? { query } : {})
      });
    });
    inject(DestroyRef).onDestroy(() => this.assistantTableState.state.set(null));
    // Sync advanced filters with entityTableService
    effect(() => {
      const filterConfig = this.advancedFilters();
      this.entityTableService.setFilterConfiguration(filterConfig);
    });
    effect(() =>
      this.entityTableService.setPathFilteredFields(this.pathBar.filteredColumnNames())
    );
    // Update the component pagination state
    effect(() => {
      const pagination = this.pagedEntityService.paginationState();
      this.paginationState.update((current) => ({
        ...current,
        page: pagination.page,
        take: pagination.take,
        total: pagination.total,
        numPages: pagination.numPages
      }));
    });
    // The flag lives on the plan in the store, so the import-from-global picker has to put
    // it back when it closes: left standing, it would make every picker on the plan
    // overview behind it offer global rows too.
    effect((onCleanup) => {
      const inEdition = this.contextType() === 'edition';
      this.store.dispatch(showGlobalDataOnly({ entityAutoCompleteGlobalData: inEdition }));
      if (!inEdition) return;
      onCleanup(() =>
        this.store.dispatch(showGlobalDataOnly({ entityAutoCompleteGlobalData: false }))
      );
    });
    // Resolve reference names for the current page's name-mode reference columns.
    // Note: this effect must never read `resolvedReferenceNames` (the signal it
    // writes to via .update()), otherwise it would re-trigger itself in a loop.
    effect(() => {
      const ctx = this.referenceContext();
      const cols = this.nameModeReferenceColumns();
      const rows = this.tableEntities();
      if (isNil(ctx) || cols.length === 0 || rows.length === 0) return;

      // Group the page's uids by target entity type (deduped, renderHook-aware).
      const uidsByType = collectReferenceUidsByType(cols, rows);

      for (const [entityType, uids] of uidsByType) {
        this.referenceNamesService
          .resolveNames$(entityType, uids, ctx)
          .subscribe((resolved) => {
            this.resolvedReferenceNames.update((prev) => {
              const next = new Map(prev);
              const merged = new Map(next.get(entityType) ?? new Map<string, string>());
              for (const [uid, name] of resolved) merged.set(uid, name);
              next.set(entityType, merged);
              return next;
            });
          });
      }
    });
  }

  ngOnInit(): void {
    this.restoreFilterState();
    // Avoid fetch the entities twice
    const subs = this.onUpdateSortOrSearch$
      .pipe(debounceTime(50))
      .subscribe(({ sortState, searchResult, filterConfiguration, path }) => {
        const dataspace = this.dataspace();
        if (!isNil(dataspace.dataspace?.id)) {
          this.fetchEntities(dataspace, searchResult, filterConfiguration, sortState, path);
        }
      });
    this.subscriptionsManager.add(subs);

    const entityModifiedSub = this.actions$
      .pipe(
        ofType(entityModified),
        // Read at emission time: the router reuses this component across entity types.
        filter(({ entityType }) => entityType === this.entityType())
      )
      .subscribe(() => setTimeout(() => this.refetchAfterWrite(), 500));
    this.subscriptionsManager.add(entityModifiedSub);

    const planDataReplacedSub = this.actions$
      .pipe(
        ofType(PlanEditActions.planDataReplaced),
        filter(({ planId }) => planId === this.plan()?.id)
      )
      .subscribe(({ planId }) => {
        // The import picker lists rows the plan does not have; only the plan's own view shows the
        // saved rows that let the overlay go.
        if (this.contextType() === 'visualization') this.overlayAwaitingRefetch = planId;
        this.refetchAfterWrite();
      });
    this.subscriptionsManager.add(planDataReplacedSub);
  }

  override ngOnDestroy(): void {
    this.clearSelectedItems();
    this.pageFetch?.subscription.unsubscribe();
    super.ngOnDestroy();
  }

  onAddToPlan(): void {
    this.addToPlanModal.set({ ...this.addToPlanModal(), isOpen: true });
  }

  confirmAddToPlan(): void {
    if (this.isSelectAllActive()) {
      this.addFilteredEntitiesToPlan();
    } else {
      this.addSelectedEntitiesToPlan();
    }
  }

  getPageContext(): 'data' | 'plan' {
    return this.plan()?.id ? 'plan' : 'data';
  }
  getAllSelectedEntityUids(): number {
    return this.bulkTargetCount();
  }
  private addFilteredEntitiesToPlan(): void {
    const entityTypeName = this.entityType();
    const planId = this.routeParams()?.get('planId');

    if (isNil(entityTypeName) || isNil(planId) || isNil(this.lastFetchRequest)) {
      throw new Error(
        'incoherent state in entity component: trying to add entities but either planId or entityTypeName is undefined'
      );
    }
    const selection = this.byFilterSelection(this.lastFetchRequest);
    if (!selection) return;
    this.dataAddedByFilter.emit({
      entityType: this.lastFetchRequest.entityType,
      planId,
      ...selection
    });
  }

  /**
   * What a by-filter action targets: the request behind the rows, except that the Path tree is
   * the composer's and the search bar's as typed, ANDed. The request carries it after
   * `pathFilterTransform`, whose sample values must never select the rows a write touches.
   *
   * `null`, with a warning shown, while either tree holds a template hole: the request left the
   * holed condition out, which would widen the write, and sent as a value a hole matches literal
   * text.
   */
  private byFilterSelection(request: EntitySelectorRequestModel): {
    filters?: FilterConfiguration;
    search?: string;
    pathFilter?: FilterExpr;
  } | null {
    if (this.pathBar.holed()) {
      this.notificationService.warning(
        this.translate.instant(
          'features.data.filter_presets.template.unfilled_action.title'
        ),
        this.translate.instant(
          'features.data.filter_presets.template.unfilled_action.message'
        )
      );
      return null;
    }
    const pathFilter = this.activeGrammar() === 'path' ? this.pathBar.filter() : undefined;
    return {
      filters: request.filters,
      search: request.search,
      ...(pathFilter ? { pathFilter } : {})
    };
  }

  private addSelectedEntitiesToPlan(): void {
    const selectedEntities = this.selectedItems().map((item) => item.raw);

    const entityTypeName = this.entityType();
    const planId = this.routeParams()?.get('planId');

    if (isNil(entityTypeName) || isNil(planId)) {
      throw new Error(
        'incoherent state in entity component: trying to add entities but either planId or entityTypeName is undefined'
      );
    }

    if (selectedEntities.length > 0) {
      this.dataAdded.emit({
        entities: selectedEntities,
        entityTypeName,
        planId
      });
    }
  }

  /**
   * Overwrites the lock of every route the current selection stands for, whatever each one
   * held before — under the select-all banner that is every matching route, not just the page
   * the table has fetched. The table refresh rides on the `entityModified` action the
   * modification service dispatches, the same way the per-route context menu's edits do.
   */
  applyRouteLock(lockCfg: RouteLockCfg): void {
    this.applyRouteBatchPatch(
      { lock_cfg: lockCfg },
      {
        successTitle: 'features.data.toolbox.lock_routes_success_title',
        successMessage: 'features.data.toolbox.lock_routes_success_message',
        successParams: {
          lock: this.translate.instant(
            ROUTE_LOCK_OPTIONS.find((option) => option.value === lockCfg)?.labelKey ?? ''
          )
        },
        errorTitle: 'features.data.toolbox.lock_routes_error_title',
        errorMessage: 'features.data.toolbox.lock_routes_error_message'
      },
      { refuseValidated: true }
    );
  }

  /** Same reach as {@link applyRouteLock}, writing the validation state instead. */
  applyRouteValidation(state: RouteValidationState): void {
    this.applyRouteBatchPatch(
      { validation_state: state },
      {
        successTitle: 'features.data.toolbox.validate_routes_success_title',
        successMessage: 'features.data.toolbox.validate_routes_success_message',
        successParams: {
          state: this.translate.instant(ROUTE_VALIDATION_STATE_KEYS[state])
        },
        errorTitle: 'features.data.toolbox.validate_routes_error_title',
        errorMessage: 'features.data.toolbox.validate_routes_error_message'
      }
    );
  }

  private applyRouteBatchPatch(
    fields: Record<string, unknown>,
    toasts: RouteBatchToasts,
    { refuseValidated = false }: { refuseValidated?: boolean } = {}
  ): void {
    const entityTypeVersion = this.entityCfg()?.versionName();
    if (isNil(entityTypeVersion) || this.bulkWriteInFlight()) return;

    this.bulkWriteInFlight.set(true);
    this.bulkTargetUids(refuseValidated)
      .then((uids) => {
        if (uids.length === 0) {
          this.bulkWriteInFlight.set(false);
          return;
        }
        this.writeRouteBatchPatch(uids, entityTypeVersion, fields, toasts);
      })
      .catch((err) => {
        if (err instanceof ValidatedRouteInTargetsError) {
          this.bulkWriteInFlight.set(false);
          this.notificationService.warning(
            this.translate.instant(toasts.errorTitle),
            this.translate.instant('features.data.toolbox.validated_routes_locked')
          );
          return;
        }
        this.reportRouteBatchFailure(err, 0, this.bulkTargetCount(), toasts);
      });
  }

  private writeRouteBatchPatch(
    uids: string[],
    entityTypeVersion: string,
    fields: Record<string, unknown>,
    toasts: RouteBatchToasts
  ): void {
    let applied = 0;

    this.entityModificationService
      .applyBatchModification$(this.entityType(), entityTypeVersion, uids, fields)
      .subscribe({
        next: (count) => (applied = count),
        complete: () => {
          // A run that completes without writing anything (no transformer resolved, so the
          // service hands back EMPTY) is a failed write, not a patch applied to zero routes.
          if (applied === 0) {
            this.reportRouteBatchFailure(
              new Error('batch modification wrote nothing'),
              0,
              uids.length,
              toasts
            );
            return;
          }

          this.bulkWriteInFlight.set(false);
          this.clearBulkSelection();
          this.notificationService.success(
            this.translate.instant(toasts.successTitle),
            this.translate.instant(toasts.successMessage, {
              count: applied,
              ...toasts.successParams
            })
          );
        },
        error: (err) => this.reportRouteBatchFailure(err, applied, uids.length, toasts)
      });
  }

  private reportRouteBatchFailure(
    err: unknown,
    applied: number,
    total: number,
    toasts: { errorTitle: string; errorMessage: string }
  ): void {
    this.bulkWriteInFlight.set(false);
    console.error('Error updating routes:', err);
    this.notificationService.error(
      this.translate.instant(toasts.errorTitle),
      this.translate.instant(toasts.errorMessage, { applied, total })
    );
  }

  /**
   * The uids a bulk write must target. The select-all banner promises every matching row, and
   * the table holds only the page it fetched, so the rest are paged in first; the cap on the
   * action keeps that walk bounded.
   */
  private async bulkTargetUids(refuseValidated: boolean): Promise<string[]> {
    const selected = [...this.selectedItemIds()];
    if (!this.isSelectAllActive()) return selected;

    const total = this.paginationState().totalItems;
    if (selected.length >= total) return selected;
    // Reported rather than shrugged off: the button is disabled over the ceiling, so reaching
    // here means the component's state does not support the write the planner just asked for.
    if (total > MAX_BULK_PATCH_ITEMS || isNil(this.lastFetchRequest)) {
      throw new Error(
        `cannot resolve the ${total} rows to write: over the ${MAX_BULK_PATCH_ITEMS} ceiling, or no fetch to page through`
      );
    }

    const entities = await this.fetchAllPages(total);
    // The toolbox sees only the loaded page; a validated route on a later page would make the
    // back end refuse its chunk after the earlier chunks were already written.
    if (
      refuseValidated &&
      entities.some((entity) =>
        isRouteValidated(entity.raw as { validation_state?: RouteValidationState })
      )
    ) {
      throw new ValidatedRouteInTargetsError();
    }
    return entities.map((entity) => entity.uid);
  }

  /**
   * Undoes the selection a bulk write consumed. Under the banner that is the whole select-all
   * state, summary walk and pagination included, which is what `onDeselectAll` tears down.
   */
  private clearBulkSelection(): void {
    if (this.isSelectAllActive()) this.onDeselectAll();
    else this.clearSelectedItems();
  }

  onDeleteEntities(): void {
    if (this.isSelectAllActive()) {
      this.deleteByFilter();
    } else {
      this.deleteByUids();
    }
  }

  private deleteByUids(): void {
    this.entityRegisterService
      .deleteByUids({
        uids: [...this.selectedItemIds()],
        entityType: this.entityType(),
        dataSpaceId: this.dataspace().dataspace?.id,
        planId: this.routeParams()?.get('planId')
      })
      .subscribe(() => {
        this.clearSelectedItems();
        setTimeout(() => this.refetchAfterWrite(), 500);
      });
  }

  private deleteByFilter(): void {
    const dataSpaceId = this.dataspace().dataspace?.id;
    const params = this.routeParams();

    if (isNil(dataSpaceId) || isNil(params) || isNil(this.lastFetchRequest)) {
      console.error(
        'incoherent state in entity component: trying to delete entities but either route params or dataspace id is undefined'
      );
      return;
    }
    const selection = this.byFilterSelection(this.lastFetchRequest);
    if (!selection) return;

    this.entityRegisterService
      .deleteByFilter({
        entityType: this.lastFetchRequest.entityType,
        filters: selection,
        dataSpaceId,
        planId: params.get('planId')
      })
      .subscribe(() => {
        this.clearSelectedItems();
        setTimeout(() => this.refetchAfterWrite(), 500);
      });
  }

  onDeselectAll(): void {
    this.isSelectAllActive.set(false);
    this.onlySelected.set(false);
    this.clearSelectedItems();
    // Reset the full-set walk so the footer doesn't briefly show stale/zero totals
    // between this synchronous reset and the async refetch.
    this.dataSummary.all?.cancel();
    this.resetPagination();
    this.paginationState.set({ ...INITIAL_PAG_STATE });
    this.getEntities();
  }

  onSelectAll(): void {
    if (this.pathBar.holed()) return;
    this.isSelectAllActive.set(true);
    if (!isNil(this.dataSummary.all) && !isNil(this.lastFetchRequest)) {
      this.dataSummary.all.updateRequest(
        this.lastFetchRequest,
        SELECT_ALL_SUMMARY_MAX_ROWS
      );
    }
  }

  loadAllRows(): void {
    const total = this.paginationState().totalItems;
    if (total > SELECT_ALL_SUMMARY_MAX_ROWS || isNil(this.lastFetchRequest)) {
      return;
    }
    // Totals: reuse the lazy walk (the summary effects already render it), capped at 500.
    this.dataSummary.all?.updateRequest(this.lastFetchRequest, SELECT_ALL_SUMMARY_MAX_ROWS);
    // Grid: page every matching row into the visible table.
    this.fetchAllPages(total)
      .then((entities) => {
        const newMap = new Map<string, EntityInterface<T>>();
        entities.forEach((entity) => newMap.set(entity.uid, entity));
        this.fetchedEntitiesInPage.set(newMap);
        this.allFetchedEntities.update((prev) => {
          const merged = new Map(prev);
          entities.forEach((entity) => merged.set(entity.uid, entity));
          return merged;
        });
        this.paginationState.set({
          currentPage: 1,
          itemsPerPage: entities.length,
          totalItems: entities.length
        });
      })
      .catch((err) => {
        console.error(err);
        this.dataSummary.signals.allStatus.set('idle');
        this.notificationService.error(
          this.translate.instant('common.error'),
          this.translate.instant('features.data.toolbox.load_all_rows_error')
        );
      });
  }

  private async fetchAllPages(total: number): Promise<EntityInterface<T>[]> {
    const pageSize = 100;
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const all: EntityInterface<T>[] = [];
    for (let page = 1; page <= pages; page++) {
      const data = await firstValueFrom(
        this.selectorsService.selectDataParsedToEntityInterface$<T>(
          this.lastFetchRequest!,
          page,
          pageSize
        )
      );
      all.push(...data.result);
      if (data.result.length === 0) break;
    }
    return all;
  }

  onFilesDropped(event: UploadClicked): void {
    this.isUploadZoneOpen.set(false);
    this.uploadService.processFilesDropped(
      event,
      this.currentEntityTransformers(),
      this.activatedRoute
    );
  }

  onSelectedOnlyChange(onlyShowSelected: boolean): void {
    this.onlySelected.set(onlyShowSelected);
    this.paginationState.set({
      currentPage: 1,
      itemsPerPage: this.nbSelectedItems(),
      totalItems: this.nbSelectedItems()
    });
  }

  onPageChange(newPage: number): void {
    this.paginationState.set({
      ...this.paginationState(),
      currentPage: newPage
    });
    this.processFetchEntitiesResult(this.pagedEntityService.goToPage<T>(newPage));
  }

  /**
   * In Path a header sort edits the Sort tab's list. The header is then put back to that list
   * when it shows anything else, a label column or another order, since the table also
   * re-sorts its page by what its header shows.
   */
  onSortChange(sortState: SortState<EntityInterface<T>>[]): void {
    if (this.activeGrammar() !== 'path') {
      this.sortState.set(sortState);
      this.classicSort.set(sortState);
      return;
    }
    const columns = this.entityTableService.sortableColMap();
    const sort = withHeaderSort(this.pathBar.sort(), sortState, columns);
    if (!sameJson(sort, this.pathBar.sort())) this.pathBar.editSort(sort);
    const shown = sortStateFor<EntityInterface<T>>(sort, columns);
    if (!sameJson(shown, sortState)) this.presetSort.set(shown);
  }

  showSelect(): boolean {
    return this.selectedItemIds().size > 0 && !!this.tableConfig().selection?.enabled;
  }

  private hasFiles(e: DragEvent): boolean {
    return e.dataTransfer?.types.includes('Files') ?? false;
  }

  /**
   * Installs the document-level drag listeners, and returns the matching removal. Re-run
   * whenever `canUpload` flips: a plan can stop refusing writes while the table is open (an
   * optimization ends), and the drop zone has to follow rather than stay dead until reload.
   */
  private setupFileDropListeners(): () => void {
    const preventDefault = (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDragOver = (e: DragEvent): void => {
      preventDefault(e);
      if (this.hasFiles(e)) this.isUploadZoneOpen.set(true);
    };
    const onDragLeave = (e: DragEvent): void => {
      preventDefault(e);
      if (!e.relatedTarget) this.isUploadZoneOpen.set(false);
    };

    document.addEventListener('dragenter', preventDefault);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);

    return () => {
      document.removeEventListener('dragenter', preventDefault);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
    };
  }

  protected addFilterOn(column: keyof EntityInterface<T>): void {
    if (this.activeGrammar() === 'path') {
      const field = this.pathFieldFor(column as string);
      if (field) this.openPillFor(field);
      return;
    }

    const filterCfg = this.filterableColMap()[column as string];
    if (isNil(filterCfg)) {
      return;
    }

    const condition = createFilterCondition(
      findFilterField(this.availableAdvancedFilters(), filterCfg.dbColName)
    );
    if (isNil(condition)) {
      return;
    }

    const currentConfig = this.advancedFilters();
    const updatedConfig = addOrReplaceConditionInConfig(currentConfig, condition);

    this.applyUserFilterEdit(updatedConfig);
    this.resetPagination();
    this.clearSelectedItems();

    this.openFilterForField.set(condition.id);
    setTimeout(() => {
      this.openFilterForField.set(null);
    }, 100);
  }

  protected removeFilterOn(column: keyof EntityInterface<T>) {
    if (this.activeGrammar() === 'path') {
      const field = this.pathFieldFor(column as string);
      if (field) this.pathBar.removeConditionsOn(field);
      return;
    }

    const filterCfg = this.filterableColMap()[column as string];
    if (isNil(filterCfg)) {
      return;
    }

    const currentConfig = this.advancedFilters();
    const updatedConfig = removeConditionFromConfig(currentConfig, filterCfg.dbColName);

    this.applyUserFilterEdit(updatedConfig);
    this.resetPagination();
    this.clearSelectedItems();
  }

  protected onAdvancedQuerySearch($event: SearchResult) {
    this.searchResult.set($event);
    const rawQuery = this.searchResult()?.rawQuery || '';
    this.resetPagination();
    this.searchQuery.set(rawQuery);
    this.clearSelectedItems();
  }

  protected onRowEdit(event: RowEvent<EntityInterface<T>>) {
    // Refused before the cell writes into the row: the back end rejects any change to a
    // validated route, and the table would otherwise keep showing the rejected value.
    if (
      this.entityType() === 'route' &&
      isRouteValidated(event.row.raw as { validation_state?: RouteValidationState })
    ) {
      this.notificationService.warning(
        this.translate.instant('features.entities.validated_route_frozen.title'),
        this.translate.instant('features.entities.validated_route_frozen.message', {
          uid: event.row.uid
        })
      );
      return;
    }

    if (event.type === 'edit') {
      const entityCfg = this.entityCfg();
      if (isNil(entityCfg)) {
        return;
      }
      const col = entityCfg
        .buildColumns()
        .find((col) => col.key == (event.column as string));
      if (isNil(col)) {
        return;
      }
      if (isNil(col.updateWithValue)) {
        return;
      }
      col.updateWithValue(event.row, event.newValue);
      this.innerSaveRow(event.row.raw as Record<string, unknown>, 'update');
    }

    if (event.type === 'askFormEdit') {
      const dataspaceId = this.dataspace().dataspace?.id;
      const entityType = this.entityType();
      const entityUid = event.row.uid;

      if (isNil(dataspaceId)) {
        console.error(
          'incoherent state in entity component: trying to navigate to entity edit but dataspace id is undefined'
        );
        return;
      }

      this.stashFilterState();
      this.navService.navigateToEntityEdit(dataspaceId, entityType, entityUid, 'form');
    }
  }

  onShowDetails(event: ShowDetails<EntityInterface<T>>): void {
    const detailsInput: EntityDetailInput = {
      entity: event.row,
      detailsEntityType: event.entityType
    };

    this.selectedItemIds.update((ids) => {
      const newIds = new Set(ids);
      newIds.add(event.row.uid);
      return newIds;
    });

    if (this.detailGroups().length === 0) {
      const hostHeight = this.detailsHost()?.nativeElement.clientHeight ?? 0;
      this.detailsPaneHeight.set(Math.round(hostHeight / 2));
    }

    this.updateDetailGroups((groups) =>
      openDetail(groups, detailsInput, this.focusedDetailGroup())
    );
    this.focusDetailGroupOf(event.row.uid);

    this.hydrateDetailedEntity(event.row.uid);
  }

  /**
   * A detail panel reads its sub-entities straight off the row (`getDetails` walks
   * `raw.actions`), which a summary row does not carry. The panel is opened immediately with
   * the row in hand and its entity swapped once the full payload lands, so the interaction
   * never waits on the network. A no-op for entities the backend serves whole.
   */
  private hydrateDetailedEntity(uid: string): void {
    const versionName = this.entityVersionName();
    const cfg = this.entityCfg();
    const dataSpaceId = this.dataspace().dataspace?.id;
    if (
      isNil(versionName) ||
      isNil(dataSpaceId) ||
      (cfg?.lazyFields?.() ?? []).length === 0
    ) {
      return;
    }
    const entityType = this.genericEntityRegistry.entityTypeFor(versionName);
    if (isNil(entityType)) {
      return;
    }

    this.hydrationService
      .hydrate$(entityType, versionName, [uid], { dataSpaceId, planId: this.plan()?.id })
      .pipe(take(1))
      .subscribe((byUid) => {
        const raw = byUid.get(uid);
        if (isNil(raw)) return;
        const [hydrated] = this.userModelsService.convertToEntityInterface<unknown>(
          [raw],
          versionName
        );
        this.detailGroups.update((groups) => replaceEntity(groups, uid, hydrated));
      });
  }

  onRemoveDetails(entityUid: string): void {
    this.updateDetailGroups((groups) => closeDetail(groups, entityUid));
  }

  onCloseAllDetails(groupIndex: number): void {
    this.updateDetailGroups((groups) => closeGroup(groups, groupIndex));
  }

  protected onActivateDetail(uid: string): void {
    this.detailGroups.update((groups) => activateDetail(groups, uid));
    this.focusDetailGroupOf(uid);
  }

  protected onSplitDetail(uid: string): void {
    const from = findGroupIndex(this.detailGroups(), uid);
    this.updateDetailGroups((groups) => moveDetail(groups, uid, from === 0 ? 1 : 0));
    this.focusDetailGroupOf(uid);
  }

  protected onDropDetail(
    event: CdkDragDrop<unknown, unknown, string>,
    toGroup: number
  ): void {
    this.updateDetailGroups((groups) =>
      moveDetail(groups, event.item.data, toGroup, event.currentIndex)
    );
    this.focusDetailGroupOf(event.item.data);
  }

  /** Keeps the focused side pointing at an existing group once a side collapses. */
  private updateDetailGroups(update: (groups: DetailGroup[]) => DetailGroup[]): void {
    const groups = update(this.detailGroups());
    this.detailGroups.set(groups);
    this.focusedDetailGroup.update((i) => Math.min(i, Math.max(groups.length - 1, 0)));
  }

  private focusDetailGroupOf(uid: string): void {
    const index = findGroupIndex(this.detailGroups(), uid);
    if (index !== -1) this.focusedDetailGroup.set(index);
  }

  protected onListItemClick(event: {
    row: EntityInterface<T>;
    column: string;
    item: unknown;
  }): void {
    if (this.activeGrammar() === 'path') {
      this.addPathListItem(event.column, String(event.item));
      return;
    }

    const filterCfg = this.filterableColMap()[event.column];
    if (isNil(filterCfg)) {
      return;
    }

    const itemValue = String(event.item);
    const condition = createFilterCondition(
      findFilterField(this.availableAdvancedFilters(), filterCfg.dbColName),
      itemValue
    );
    if (isNil(condition)) {
      return;
    }

    const currentConfig = this.advancedFilters();
    const updatedConfig = addOrReplaceConditionInConfig(currentConfig, condition);

    this.applyUserFilterEdit(updatedConfig);
    this.resetPagination();
    this.clearSelectedItems();

    this.openFilterForField.set('');
    setTimeout(() => {
      this.openFilterForField.set(null);
    }, 100);
  }

  /** The pills row sits behind the funnel, so it is shown before the field's popover opens. */
  private openPillFor(field: FilterField): void {
    this.showAdvancedFiltering.set(true);
    afterNextRender(() => this.pills()?.openField(field), { injector: this.injector });
  }
  /**
   * A column without a Classic filter config (a reference list such as compatible trailers) still
   * names its field by its key, which Path can filter on.
   */
  private pathFieldFor(column: string): FilterField | undefined {
    const dbColName =
      this.filterableColMap()[column]?.dbColName ?? column.replace(/^raw\./, '');
    return this.pathBar.fieldForColumn(dbColName);
  }

  private addPathListItem(column: string, item: string): void {
    const field = this.pathFieldFor(column);
    if (!field) return;
    if (!this.pathBar.addListItem(field, item)) this.openPillFor(field);
  }

  protected readonly isColumnFilterable = (column: string): boolean =>
    this.activeGrammar() === 'path'
      ? !isNil(this.pathFieldFor(column))
      : !isNil(this.filterableColMap()[column]);

  protected readonly includeInFilterStatusFor = (
    targetEntityType: string,
    uid: string
  ): IncludeInFilterStatus =>
    this.canIncludeInFilter()
      ? includeInFilterStatus(
          this.activeGrammar() === 'path'
            ? this.pathBar.shownPayload()
            : this.filterPresetPayload(),
          this.entityType(),
          targetEntityType,
          uid
        )
      : 'hidden';

  /**
   * Without a uid field the included condition could be neither shown nor saved. A template being
   * authored takes none: it would save that one row into every later use of the template.
   */
  private canIncludeInFilter(): boolean {
    return this.activeGrammar() === 'path'
      ? !isNil(this.pathBar.uidField()) && !this.filterPresetsStore.templateAuthoring()
      : !isNil(findFilterField(this.availableAdvancedFilters(), 'uid'));
  }

  /**
   * Widens the table's whole filter, search-bar qualifiers included, to also show `uid`. Either
   * search bar gives up its qualifiers to the filter being widened: left alone, it would keep
   * ANDing them in front of the OR.
   */
  protected onIncludeInFilter(event: { uid: string; targetEntityType: string }): void {
    this.showAdvancedFiltering.set(true);
    if (this.activeGrammar() === 'path') {
      this.pathBar.includeUid(event.uid);
      return;
    }

    const filters = includeUidInFilter(
      this.filterPresetPayload(),
      event.uid,
      event.targetEntityType
    );

    this.searchResult.set(null);
    this.searchQuery.set('');
    this.applyUserFilterEdit(filters);
    this.resetPagination();
    this.clearSelectedItems();
  }

  private innerSaveRow<T extends Record<string, unknown>>(
    rawValues: T,
    operation: 'create' | 'update'
  ): void {
    const dataSpaceId = this.dataspace().dataspace?.id;
    const transformer = this.editionTransformerSchema()?.transformerInfo.transformer;
    const entityTypeVersion = this.entityVersionName();

    if (isNil(dataSpaceId) || isNil(transformer)) {
      console.error(
        'incoherent state in entity component: trying to update entity but either dataspace id or transformer info is undefined'
      );
      return;
    }

    const updateRequest: CreateOrUpdateGlobalEntityRequest = {
      data: rawValues,
      transformer: {
        name: transformer.name,
        version: transformer.version
      },
      dataSpaceId,
      entityTypeVersion
    };

    const createOrUpdate: Observable<void | { uid?: string }> =
      operation === 'create'
        ? this.entityModificationService.planAwareCreate$(this.entityType(), updateRequest)
        : this.entityModificationService.planAwareUpdate$(this.entityType(), updateRequest);

    createOrUpdate
      .pipe(
        take(1),
        catchError((err) => {
          console.error('Error updating entity:', err);
          return of();
        })
      )
      .subscribe(() => {
        this.clearSelectedItems();
        this.notificationService.success(
          this.translate.instant(`features.entities.${operation}.title`),
          this.translate.instant(`features.entities.${operation}.message`, {
            uid: rawValues['uid']
          }),
          5000
        );
        setTimeout(() => this.refetchAfterWrite(), 500);
      });
  }

  newEntityFromForm(): void {
    const dataspaceId = this.dataspace().dataspace?.id;
    const entityType = this.entityType();

    if (isNil(dataspaceId)) {
      console.error(
        'incoherent state in entity component: trying to navigate to new entity but dataspace id is undefined'
      );
      return;
    }

    this.stashFilterState();
    this.navService.navigateToNewEntity(dataspaceId, entityType, 'form');
  }

  private resetPagination(): void {
    this.pagedEntityService.resetPagination();
    this.paginationState.set({ ...INITIAL_PAG_STATE });
  }

  protected onFilterChanges($event: FilterConfiguration) {
    this.applyUserFilterEdit($event);
    this.resetPagination();
    this.clearSelectedItems();
  }

  /** The same consequences as a classic filter edit: the preset no longer describes it, page one, no ticks. */
  private afterPathEdit(): void {
    this.filterPresetsStore.detach();
    this.resetPagination();
    this.clearSelectedItems();
  }

  /**
   * Every hand-made change to the filters goes through here so the active preset is
   * always detached: once the criteria are edited they no longer describe that preset.
   * Preset application writes `advancedFilters` directly instead, which is what keeps the
   * chip selected.
   */
  private applyUserFilterEdit(config: FilterConfiguration | null): void {
    this.advancedFilters.set(config);
    this.filterPresetsStore.detach();
  }

  /**
   * Which list a stashed filter state belongs to.
   *
   * The dataspace comes from the URL rather than from the store, because on the way back
   * the store has not loaded it yet while the URL — the list's or the edition form's, both
   * carry the same `/ds/:id` — always has it.
   */
  private filterHandoffContext(): EntityListFilterContext {
    return {
      entityType: this.entityType(),
      dataspaceId: extractDataspaceIdFromUrl(this.router.url),
      planId: this.routeParams()?.get('planId') ?? null
    };
  }

  /**
   * Hands the live filtering state to the next mount of this list.
   *
   * Called only when leaving for the edition form, which is what keeps the filter from
   * outliving that round-trip: any other way back to the list finds nothing stashed.
   */
  private stashFilterState(): void {
    // The way back restores into a store that is no longer authoring, so the holes must not travel.
    if (this.filterPresetsStore.templateAuthoring()) this.abandonTemplate();
    this.filterHandoff.stash({
      ...this.filterHandoffContext(),
      searchResult: this.searchResult(),
      searchQuery: this.searchQuery(),
      advancedFilters: this.advancedFilters(),
      sort: this.sortState().map(({ column, direction }) => ({
        column: String(column),
        direction
      })),
      showAdvancedFiltering: this.showAdvancedFiltering(),
      activePresetId: this.filterPresetsStore.activePresetId(),
      presetGrammar: this.pathBar.presetGrammar(),
      path:
        this.activeGrammar() === 'path'
          ? {
              filter: this.pathBar.wire(),
              sort: this.pathBar.sort(),
              bar: this.pathBar.bar()
            }
          : null
    });
  }

  /**
   * Re-applies the state stashed when this list opened the edition form, so saving (or
   * cancelling) an edit comes back to the rows the user was looking at.
   *
   * Seeds the same signals as `applyPresetToTable`, including the sort through the
   * table's own input; pagination is deliberately left at page one, since a row that no
   * longer matches the filter changes the result count under the remembered page.
   */
  private restoreFilterState(): void {
    const snapshot = this.filterHandoff.consume(this.filterHandoffContext());
    if (isNil(snapshot)) {
      return;
    }

    this.advancedFilters.set(snapshot.advancedFilters);
    this.searchQuery.set(snapshot.searchQuery);
    this.searchResult.set(snapshot.searchResult);
    this.showAdvancedFiltering.set(snapshot.showAdvancedFiltering);
    if (snapshot.sort.length > 0) {
      this.classicSort.set(snapshot.sort as SortState<EntityInterface<T>>[]);
    }
    this.pathBar.presetGrammar.set(snapshot.presetGrammar);
    if (snapshot.path) {
      this.pathBar.load(snapshot.path.filter, snapshot.path.sort);
      this.pathBar.loadBar(snapshot.path.bar);
    }
    this.filterPresetsStore.seedRestoredState(snapshot.activePresetId);
  }

  private onFilterPresetApplied(applied: AppliedFilterPreset) {
    const authoring = this.filterPresetsStore.templateAuthoring();
    this.pathBar.applyPreset(this.withColumnSortInPathSort(applied), {
      refuse: () => {
        this.filterPresetsStore.detach();
        // A default Path preset is applied on every open of this entity where Path is off.
        if (applied.automatic) return;
        this.notificationService.warning(
          this.translate.instant('features.data.path_filter.preset_unavailable.title'),
          this.translate.instant('features.data.path_filter.preset_unavailable.message')
        );
      },
      apply: (preset) => this.applyPresetToTable(preset)
    });
    // Only after the bar holds the preset: abandoning first would strip the draft's holes as a
    // hand edit and detach the preset the store has just marked active.
    if (authoring) this.abandonTemplate();
  }

  /**
   * A preset's column sort, as Path reads it: the end of its Sort-tab list, label columns left
   * out. A preset holding nothing but root sorts restores those that name a column as columns;
   * Classic ignores `pathSort`.
   */
  private withColumnSortInPathSort(applied: AppliedFilterPreset): AppliedFilterPreset {
    const columnSort = qualifiersForSortState(
      (applied.sort ?? []) as SortState<EntityInterface<T>>[],
      this.entityTableService.sortableColMap()
    ).map(toOrderBy);
    if (columnSort.length === 0) return applied;
    return { ...applied, pathSort: [...(applied.pathSort ?? []), ...columnSort] };
  }

  /** The labels a preset sorts on, which no Path sort can name. */
  private labelSortsOf(sort: RestoredSort[] | undefined): string[] {
    const columns = this.entityTableService.sortableColMap();
    return (sort ?? []).flatMap(({ column }) => {
      const labelKey = columns[column]?.labelKey;
      return isNil(labelKey) ? [] : [labelKey];
    });
  }

  /**
   * The table's share of a preset: the bar already holds its Path part. A Classic preset carries
   * its search-bar qualifiers as filter conditions, so everything but the free text goes to the
   * advanced filters. The 50 ms debounce on the refetch trigger collapses the signal writes into
   * a single request.
   */
  private applyPresetToTable(applied: AppliedFilterPreset): void {
    const isPath = applied.grammar === 'path';
    const droppedFields = isPath
      ? [...new Set([...applied.droppedFields, ...this.labelSortsOf(applied.sort)])]
      : applied.droppedFields;
    if (droppedFields.length > 0) {
      this.notificationService.warning(
        this.translate.instant('features.data.filter_presets.dropped_fields.title'),
        this.translate.instant('features.data.filter_presets.dropped_fields.message', {
          fields: droppedFields.join(', ')
        })
      );
    }

    const search = isPath ? '' : (applied.search ?? '');
    this.advancedFilters.set(isPath ? null : (applied.filters ?? null));
    this.searchQuery.set(search);
    this.searchResult.set(
      search.length === 0
        ? null
        : {
            ...SearchResultBuilder.empty(),
            rawQuery: search,
            textTerms: search.split(/\s+/).filter((term) => term.length > 0)
          }
    );
    if (!isPath && !isNil(applied.filters)) {
      this.showAdvancedFiltering.set(true);
    }

    // A preset without a stored sort leaves the current ordering as it is; only one that
    // carries one reseeds the table. In Path the header follows the Sort tab instead.
    if (!isPath && !isNil(applied.sort)) {
      this.classicSort.set(applied.sort as SortState<EntityInterface<T>>[]);
    }

    this.resetPagination();
    this.clearSelectedItems();
  }
}

/** The windows the visualization defaults preference can seed. */
type SeedableVisualizationModule = 'map' | 'timeline' | 'calendar' | 'list';

/**
 * Resolves the entity-view visualization defaults for a mount from the current
 * entity type's stored (possibly-partial) preference: the master toggle state
 * and which windows should open. A window is only opened when the user prefers
 * it AND the current entity actually supports it, and the master toggle only
 * turns on when the entity supports at least one visualization. Pure so the
 * seeding logic can be unit-tested without the component.
 */
export function resolveVisualizationDefaults(
  settings: VisualizationPreferences | undefined,
  supported: Set<VisualizationModule>
): { enabled: boolean; windows: Record<SeedableVisualizationModule, boolean> } {
  const prefs = resolveVisualizationPreferences(settings);
  return {
    enabled: prefs.enabledByDefault && supported.size > 0,
    windows: {
      map: prefs.defaultOpenViews.map && supported.has('map'),
      timeline: prefs.defaultOpenViews.timeline && supported.has('timeline'),
      calendar: prefs.defaultOpenViews.calendar && supported.has('calendar'),
      list: prefs.defaultOpenViews.list && supported.has('list')
    }
  };
}
